import React, { useEffect, useState, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { formatDateTime, formatDate } from "../utils/dateUtils";
import { groupService } from "../services/groupService";

export default function RFQTimeline({ rfqNumber, onClose, onOpenChat, clientId, onUpdate }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupError, setGroupError] = useState(null);
  
  // Trade intervention state
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterQuantity, setCounterQuantity] = useState("");
  const [counterNotes, setCounterNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Load groups function
  const loadGroups = useCallback(async () => {
    if (!clientId) return;
    
    try {
      setLoadingGroups(true);
      const data = await groupService.getAllGroups(clientId);
      setGroups(data);
    } catch (err) {
      console.error('[RFQTimeline] Failed to load groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  }, [clientId]);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, [clientId, loadGroups]);

  // Extract loadEvents as a standalone function for reuse
  const loadEvents = useCallback(async () => {
    if (!rfqNumber) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const url = getApiUrl(`/api/events/rfq/${encodeURIComponent(rfqNumber)}`);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch events: ${res.status}`);
      const data = await res.json();
        
        // Extract events array from response (handle both array and object responses)
        const eventsArray = Array.isArray(data) ? data : (data.events || []);

        // Helper: normalize an event to surface quote fields that may be nested in
        // different shapes (quote, quotes array, newState/nextState, data payload, etc.)
        const normalizeEvent = (ev) => {
          const get = (obj, ...paths) => {
            for (const p of paths) {
              if (!obj) break;
              obj = obj[p];
            }
            return obj;
          };

          console.log('Normalizing event:', ev);

          const previousState = ev.previousState || ev.oldState || get(ev, 'previous', 'state') || get(ev, 'data', 'previousState') || get(ev, 'previousState');
          const nextState = ev.nextState || ev.newState || get(ev, 'next', 'state') || get(ev, 'data', 'nextState') || get(ev, 'nextState');

          // Surface-only lookup: only consider quote if it's directly present on the event
          // (per requirements we should not recursively search the payload).
          const quoteObj = ev.quote || null;

          const quotePrice = quoteObj?.price ?? ev.quotePrice ?? get(ev, 'data', 'quotePrice') ?? null;
          const quoteId = quoteObj?.id ?? ev.quoteId ?? get(ev, 'data', 'quoteId') ?? null;
          const deliveryTime = quoteObj?.deliveryTime ?? ev.deliveryTime ?? get(ev, 'data', 'deliveryTime') ?? null;
          const terms = quoteObj?.terms ?? ev.terms ?? get(ev, 'data', 'terms') ?? null;
          const notes = quoteObj?.notes ?? ev.notes ?? get(ev, 'data', 'notes') ?? null;
          const validUntil = quoteObj?.validUntil ?? ev.validUntil ?? get(ev, 'data', 'validUntil') ?? null;
          const rfqNotes = ev.rfqNotes ?? get(ev, 'rfq', 'notes') ?? get(ev, 'data', 'rfqNotes') ?? null;

          return {
            ...ev,
            previousState,
            nextState,
            quotePrice,
            quoteId,
            deliveryTime,
            terms,
            notes,
            validUntil,
            rfqNotes,
          };
        };

        // Sort events by createdAt descending (newest first) then normalize
        const sortedEvents = (eventsArray || [])
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map(normalizeEvent);

        // Backfill quote fields: build a map of quoteId -> first-seen non-empty details
        const quoteMap = new Map();
        for (const ev of sortedEvents) {
          const qid = ev.quoteId ?? ev.quote?.id ?? ev.id ?? null;
          if (qid != null) {
            const key = String(qid);
            const entry = quoteMap.get(key) || { quotePrice: null, deliveryTime: null, terms: null, notes: null, validUntil: null };
            if (entry.quotePrice == null && ev.quotePrice != null) entry.quotePrice = ev.quotePrice;
            if (entry.deliveryTime == null && ev.deliveryTime != null) entry.deliveryTime = ev.deliveryTime;
            if (entry.terms == null && ev.terms != null) entry.terms = ev.terms;
            if (entry.notes == null && ev.notes != null) entry.notes = ev.notes;
            if (entry.validUntil == null && ev.validUntil != null) entry.validUntil = ev.validUntil;
            quoteMap.set(key, entry);
          }
        }

        // Apply backfill so events that reference a quoteId but lack price get populated
        const filled = sortedEvents.map(ev => {
          if (ev.quoteId != null) {
            const key = String(ev.quoteId);
            const info = quoteMap.get(key);
            if (info) {
              return {
                ...ev,
                quotePrice: ev.quotePrice ?? info.quotePrice,
                deliveryTime: ev.deliveryTime ?? info.deliveryTime,
                terms: ev.terms ?? info.terms,
                notes: ev.notes ?? info.notes,
                validUntil: ev.validUntil ?? info.validUntil,
              };
            }
          }
          return ev;
        });

        setEvents(filled);

        // If any events still reference a quoteId but lack price details, try fetching
        // the quote directly from the server (/api/quotes/{id}) and merge results.
        (async () => {
          try {
            const missingIds = Array.from(new Set(
              filled
                .filter(ev => ev.quoteId != null && (ev.quotePrice === null || ev.quotePrice === undefined))
                .map(ev => String(ev.quoteId))
            ));

            if (missingIds.length === 0) return;

            // Fetch quote details in parallel (limit could be added if needed)
            const fetches = missingIds.map(id =>
              fetch(getApiUrl(`/api/quotes/${encodeURIComponent(id)}`)).then(async (res) => {
                if (!res.ok) throw new Error(`Quote fetch failed ${res.status}`);
                const d = await res.json();
                return { id, data: d };
              }).catch(err => {
                console.debug('[RFQTimeline] Failed to fetch quote', id, err.message || err);
                return { id, data: null };
              })
            );

            const results = await Promise.all(fetches);
            const remoteMap = new Map();
            for (const r of results) {
              if (r && r.data) {
                // Accept different shapes: object may be { price } or { quote: { price } }
                const q = r.data.quote || r.data;
                const price = q?.price ?? q?.quotePrice ?? null;
                const deliveryTime = q?.deliveryTime ?? null;
                const terms = q?.terms ?? null;
                const notes = q?.notes ?? null;
                const validUntil = q?.validUntil ?? null;
                remoteMap.set(String(r.id), { quotePrice: price, deliveryTime, terms, notes, validUntil });
              }
            }

            if (remoteMap.size === 0) return;

            // Merge remote data into existing events
            setEvents(prev => prev.map(ev => {
              if (ev.quoteId != null) {
                const info = remoteMap.get(String(ev.quoteId));
                if (info) {
                  return {
                    ...ev,
                    quotePrice: ev.quotePrice ?? info.quotePrice,
                    deliveryTime: ev.deliveryTime ?? info.deliveryTime,
                    terms: ev.terms ?? info.terms,
                    notes: ev.notes ?? info.notes,
                    validUntil: ev.validUntil ?? info.validUntil,
                  };
                }
              }
              return ev;
            }));
          } catch (err) {
            console.debug('[RFQTimeline] remote quote merge failed', err?.message || err);
          }
        })();
      } catch (e) {
        console.error("[RFQTimeline] Failed to load events:", e);
        setError(e.message || "Failed to load timeline events");
      } finally {
        setLoading(false);
      }
    }, [rfqNumber]);

  useEffect(() => {
    loadEvents();
  }, [rfqNumber, loadEvents]);

  const getStateColor = (state) => {
    const stateColors = {
      "pending": "bg-blue-100 text-blue-800 border-blue-300",
      "quoted": "bg-purple-100 text-purple-800 border-purple-300",
      "countered": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "last look": "bg-indigo-100 text-indigo-800 border-indigo-300",
      "accepted": "bg-green-100 text-green-800 border-green-300",
      "awarded": "bg-teal-100 text-teal-800 border-teal-300",
      "rejected": "bg-red-100 text-red-800 border-red-300",
    };
    const norm = state?.toLowerCase().replace(/[_-]/g, ' ');
    return stateColors[norm] || stateColors[state?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setGroupError('Please enter a group name');
      return;
    }

    if (!clientId) {
      setGroupError('User not authenticated');
      return;
    }

    try {
      setCreatingGroup(true);
      setGroupError(null);
      const newGroup = await groupService.createGroup(clientId, newGroupName.trim());
      await loadGroups();
      setNewGroupName("");
      setShowCreateGroup(false);
      // Optionally select the newly created group
      if (newGroup && newGroup.id) {
        setSelectedGroupId(newGroup.id);
      }
    } catch (err) {
      console.error('[RFQTimeline] Failed to create group:', err);
      setGroupError(err.message || 'Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleAddToGroup = async () => {
    if (!selectedGroupId) {
      setGroupError('Please select a group');
      return;
    }

    if (!rfqNumber) {
      setGroupError('Cannot add to group: No RFQ number');
      return;
    }

    try {
      setGroupError(null);
      await groupService.addRFQToGroup(clientId, selectedGroupId, rfqNumber);
      setSelectedGroupId("");
      alert('RFQ added to group successfully');
    } catch (err) {
      console.error('[RFQTimeline] Failed to add RFQ to group:', err);
      setGroupError(err.message || 'Failed to add RFQ to group');
    }
  };

  // Trade intervention handlers
  const handleAccept = async () => {
    if (!rfqNumber) {
      setActionError("Cannot accept: No RFQ number");
      return;
    }

    if (!window.confirm(`Accept RFQ ${rfqNumber}?`)) {
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      const response = await fetch(getApiUrl(`/api/rfq/${rfqNumber}/accept?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rfqId: rfqNumber,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to accept RFQ: ${response.status}`);
      }

      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("RFQ accepted:", data);

      // Wait a moment for backend to process the state change
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload events to show updated state
      await loadEvents();
      // Reload archive list if callback provided
      if (onUpdate) {
        await onUpdate();
      }
    } catch (err) {
      console.error("Accept error:", err);
      setActionError(err.message || "Failed to accept RFQ");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rfqNumber) {
      setActionError("Cannot reject: No RFQ number");
      return;
    }

    if (!window.confirm(`Are you sure you want to reject RFQ ${rfqNumber}?`)) {
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      const response = await fetch(getApiUrl(`/api/rfq/${rfqNumber}/reject?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rfqId: rfqNumber,
          reason: "Rejected by client",
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to reject RFQ: ${response.status}`);
      }

      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("RFQ rejected:", data);

      // Wait a moment for backend to process the state change
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload events to show updated state
      await loadEvents();
      // Reload archive list if callback provided
      if (onUpdate) {
        await onUpdate();
      }
    } catch (err) {
      console.error("Reject error:", err);
      setActionError(err.message || "Failed to reject RFQ");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCounter = async () => {
    if (!rfqNumber) {
      setActionError("Cannot counter: No RFQ number");
      return;
    }

    console.log('[RFQTimeline] Counter values before parsing:', { counterPrice, counterQuantity, counterNotes });
    
    const price = parseFloat(counterPrice);
    const quantity = parseFloat(counterQuantity);

    console.log('[RFQTimeline] Parsed counter values:', { price, quantity, isNaNPrice: isNaN(price), isNaNQuantity: isNaN(quantity) });

    if (isNaN(price) || price <= 0) {
      setActionError("Please enter a valid price");
      return;
    }

    if (isNaN(quantity) || quantity <= 0) {
      setActionError("Please enter a valid quantity");
      return;
    }

    setActionLoading(true);
    setActionError(null);

    const payload = {
      rfqId: rfqNumber,
      price,
      quantity,
      notes: counterNotes.trim(),
      timestamp: new Date().toISOString(),
    };
    
    console.log('[RFQTimeline] Sending counter offer payload:', payload);

    try {
      const response = await fetch(getApiUrl(`/api/rfq/${rfqNumber}/counter?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit counter: ${response.status}`);
      }

      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("Counter submitted:", data);

      // Wait a moment for backend to process the state change
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload events to show updated state
      await loadEvents();
      setShowCounterForm(false);
      setCounterPrice("");
      setCounterQuantity("");
      setCounterNotes("");
      // Reload archive list if callback provided
      if (onUpdate) {
        await onUpdate();
      }
    } catch (err) {
      console.error("Counter error:", err);
      setActionError(err.message || "Failed to submit counter offer");
    } finally {
      setActionLoading(false);
    }
  };

  const getStateLabel = (state) => {
    if (!state) return "Unknown";
    const norm = state?.toLowerCase().replace(/[_-]/g, ' ');
    if (norm.includes('last')) return 'Last Look';
    const labels = {
      "pending": "New Request",
      "quoted": "Quoted",
      "countered": "Countered",
      "accepted": "Accepted",
      "awarded": "Awarded",
      "rejected": "Rejected",
    };
    return labels[norm] || labels[state?.toLowerCase()] || state || "Unknown";
  };

  // Get current state from most recent event
  const getCurrentState = () => {
    if (events.length === 0) return null;
    const mostRecent = events[0]; // Events are sorted newest first
    return mostRecent.nextState || mostRecent.previousState || null;
  };

  // Get quote information from most recent quoted event
  const getCurrentQuote = () => {
    const quotedEvent = events.find(e => 
      e.nextState?.toLowerCase() === 'quoted' && 
      (e.quotePrice !== null && e.quotePrice !== undefined)
    );
    return quotedEvent;
  };

  const currentState = getCurrentState();
  const currentQuote = getCurrentQuote();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-20">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            RFQ Timeline - #{rfqNumber}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6 text-gray-900 dark:text-gray-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
  <div className="p-6 relative z-0">
          {loading && (
            <div className="text-center text-gray-600 dark:text-gray-400 py-8">Loading timeline...</div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-200">
              {error}
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="text-center text-gray-600 dark:text-gray-400 py-8">No events found for this RFQ.</div>
          )}

          {!loading && !error && events.length > 0 && (
            <div className="relative">
              {/* Timeline vertical line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-600"></div>

              {/* Timeline events */}
              <div className="space-y-6">
                {events.map((event, index) => (
                  <div key={event.id || index} className="relative pl-20">
                    {/* Timeline dot */}
                    {console.log('Rendering event:', event)}
                    <div className="absolute left-6 w-5 h-5 rounded-full bg-white dark:bg-gray-800 border-4 border-blue-500 dark:border-blue-600"></div>

                    {/* Event card */}
                    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {event.previousState && (
                            <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStateColor(event.previousState)}`}>
                              {getStateLabel(event.previousState)}
                            </span>
                          )}
                          {event.previousState && event.nextState && (
                            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                          {event.nextState && (
                            <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStateColor(event.nextState)}`}>
                              {getStateLabel(event.nextState)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(event.createdAt)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {event.lastInteractedBy && (
                          <div className="col-span-2">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Last Interacted By:</span>
                            <div className="text-gray-900 dark:text-gray-100 font-semibold">
                              {event.lastInteractedBy}
                            </div>
                          </div>
                        )}
                        
                        {event.dealerName && (
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Dealer:</span>
                            <div className="text-gray-900 dark:text-gray-100">
                              {event.dealerName}
                              {event.dealerCompany && ` (${event.dealerCompany})`}
                            </div>
                          </div>
                        )}
                        
                        {event.clientName && (
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Client:</span>
                            <div className="text-gray-900 dark:text-gray-100">
                              {event.clientName}
                              {event.clientCompany && ` (${event.clientCompany})`}
                            </div>
                          </div>
                        )}
                        
                        {event.quotePrice !== null && event.quotePrice !== undefined && (
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Quote Price:</span>
                            <div className="text-gray-900 dark:text-gray-100 font-semibold">
                              ${event.quotePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                      
                        {event.validUntil && (
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Valid Until:</span>
                            <div className="text-gray-900 dark:text-gray-100">{formatDate(event.validUntil)}</div>
                          </div>
                        )}

                        {event.deliveryTime && (
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Delivery Time:</span>
                            <div className="text-gray-900 dark:text-gray-100">{event.deliveryTime}</div>
                          </div>
                        )}
                        
                        {event.quoteId && (
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Quote ID:</span>
                            <div className="text-gray-900 dark:text-gray-100">{event.quoteId}</div>
                          </div>
                        )}
                      </div>

                      {event.terms && (
                        <div className="mt-3 text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Terms:</span>
                          <div className="text-gray-900 dark:text-gray-100 mt-1">{event.terms}</div>
                        </div>
                      )}

                      {event.notes && (
                        <div className="mt-3 text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Quote Notes:</span>
                          <div className="text-gray-900 dark:text-gray-100 mt-1 whitespace-pre-wrap">{event.notes}</div>
                        </div>
                      )}
                      
                      {event.rfqNotes && (
                        <div className="mt-3 text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300">RFQ Notes:</span>
                          <div className="text-gray-900 dark:text-gray-100 mt-1 whitespace-pre-wrap">{event.rfqNotes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          {/* Action Error Message */}
          {actionError && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-200 text-sm">
              {actionError}
            </div>
          )}

          {/* Trade Intervention Actions */}
          {clientId && rfqNumber && (
            <div className="mb-4 space-y-3">
              {/* Counter Offer Form */}
              {showCounterForm && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Counter Offer</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Counter Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={counterPrice}
                      onChange={(e) => setCounterPrice(e.target.value)}
                      placeholder="Enter counter price"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={actionLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Counter Quantity
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={counterQuantity}
                      onChange={(e) => setCounterQuantity(e.target.value)}
                      placeholder="Enter counter quantity"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={actionLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={counterNotes}
                      onChange={(e) => setCounterNotes(e.target.value)}
                      placeholder="Add any notes..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleCounter}
                      disabled={actionLoading}
                      className="flex-1 px-4 py-2 bg-orange-600 dark:bg-orange-700 text-white font-medium rounded hover:bg-orange-700 dark:hover:bg-orange-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? "Submitting..." : "Submit Counter"}
                    </button>
                    <button
                      onClick={() => {
                        setShowCounterForm(false);
                        setCounterPrice("");
                        setCounterQuantity("");
                        setCounterNotes("");
                        setActionError(null);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!showCounterForm && currentState && currentState.toLowerCase() !== "rejected" && currentState.toLowerCase() !== "accepted" && currentState.toLowerCase() !== "awarded" && (
                <div className="flex gap-2">
                  {/* Accept Button - Only show for Quoted state */}
                  {currentState.toLowerCase() === "quoted" && (
                    <button
                      onClick={handleAccept}
                      disabled={actionLoading}
                      className="flex-1 px-4 py-2 bg-green-600 dark:bg-green-700 text-white font-medium rounded hover:bg-green-700 dark:hover:bg-green-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? "Accepting..." : "Accept Quote"}
                    </button>
                  )}

                  {/* Counter Button */}
                  <button
                    onClick={() => {
                      // Pre-fill with current quote price if available
                      if (currentQuote && currentQuote.quotePrice) {
                        setCounterPrice(String(currentQuote.quotePrice));
                      }
                      // Try to get quantity from events
                      const eventWithQty = events.find(e => e.quantity);
                      if (eventWithQty && eventWithQty.quantity) {
                        setCounterQuantity(String(eventWithQty.quantity));
                      }
                      setShowCounterForm(true);
                    }}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-orange-600 dark:bg-orange-700 text-white font-medium rounded hover:bg-orange-700 dark:hover:bg-orange-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Submit Counter Offer
                  </button>

                  {/* Reject Button */}
                  <button
                    onClick={handleReject}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-red-600 dark:bg-red-700 text-white font-medium rounded hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? "Rejecting..." : "Reject"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Add to Group Section */}
          {clientId && rfqNumber && (
            <div className="mb-4 space-y-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Add to Group
              </h4>
              
              {groupError && (
                <div className="text-sm text-red-600 dark:text-red-400">
                  {groupError}
                </div>
              )}
              
              <div className="flex gap-2">
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={loadingGroups || creatingGroup}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">Select a group...</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.rfqs?.length || 0} RFQs)
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddToGroup}
                  disabled={!selectedGroupId || loadingGroups || creatingGroup}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white font-medium rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
              
              {!showCreateGroup && (
                <button
                  onClick={() => setShowCreateGroup(true)}
                  disabled={loadingGroups || creatingGroup}
                  className="w-full px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Group
                </button>
              )}
              
              {showCreateGroup && (
                <div className="space-y-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Group Name
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !creatingGroup) {
                        handleCreateGroup();
                      }
                    }}
                    placeholder="Enter group name..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={creatingGroup}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateGroup}
                      disabled={creatingGroup || !newGroupName.trim()}
                      className="flex-1 px-3 py-2 bg-green-600 dark:bg-green-700 text-white font-medium rounded hover:bg-green-700 dark:hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {creatingGroup ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateGroup(false);
                        setNewGroupName("");
                        setGroupError(null);
                      }}
                      disabled={creatingGroup}
                      className="px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between gap-3">
            {onOpenChat && (
              <button
                onClick={() => {
                  const rfq = { rfqNumber };
                  onOpenChat(rfq);
                  onClose();
                }}
                className="px-6 py-2 bg-blue-600 dark:bg-blue-700 text-white font-medium rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
              >
                Open Chat
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
